import React from 'react';
import CodeBlock from '../../CodeBlock';
import { PenTool, Timer } from 'lucide-react';

const SOPGenerationWorkflow = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <div className="workflow-section">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4 text-white">SOP Generation</h1>
        <p className="text-lg md:text-xl text-gray-300 leading-7 max-w-[65ch]">
          Generate structured Standard Operating Procedures from your documents using our multi-agent pipeline.
          SOPs are automatically organized into steps with detailed procedures.
        </p>
      </div>

      <div className="card mb-6 bg-purple-900/20 border border-purple-700/50">
        <h3 className="text-lg font-bold tracking-tight text-purple-400 mb-3">How it works</h3>
        <p className="text-sm text-gray-300 leading-6 mb-4">
          SOP generation uses a multi-agent pipeline that:
        </p>
        <ul className="space-y-2 text-sm text-gray-300 list-disc list-inside ml-2">
          <li>Analyzes your namespace documents using semantic search</li>
          <li>Extracts relevant procedures and processes</li>
          <li>Structures content into clear, actionable steps</li>
          <li>Generates detailed procedures for each step</li>
          <li>Organizes everything into a complete SOP</li>
        </ul>
      </div>

      <div className="space-y-6">
        {/* Step 1: Prerequisites */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">1</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Prerequisites</h3>
              <p className="text-sm text-gray-400 leading-6">
                Before generating an SOP, ensure you have:
              </p>
            </div>
          </div>

          <div className="ml-14">
            <ul className="space-y-2 text-sm text-gray-300 list-disc list-inside ml-2">
              <li>A <strong className="text-gray-200">project</strong> to organize your SOPs</li>
              <li>A <strong className="text-gray-200">namespace</strong> with processed documents</li>
              <li>At least one document with <code className="text-purple-400 bg-gray-800 px-1 py-0.5 rounded">COMPLETED</code> ingestion status</li>
            </ul>
          </div>
        </div>

        {/* Step 2: Create Generation Task */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">2</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Create an SOP generation task</h3>
              <p className="text-sm text-gray-400 leading-6">
                Start SOP generation by creating a task. Provide a clear, descriptive prompt that explains what you want to generate.
              </p>
            </div>
          </div>

          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X POST ${BASE_URL}/projects/{project_uuid}/sopGenerationTasks \\
  -H "X-API-Key: your-email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "source_namespace_uuid": "{namespace_uuid}",
    "prompt": "Generate a comprehensive standard operating procedure for handling customer support escalations. Include steps for identification, triage, escalation paths, and resolution."
  }'`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`{
  "uuid": "e5f6a7b8-c9d0-1234-ef01-345678901234",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "status": "PROCESSING",
  "prompt": "Generate a comprehensive standard operating procedure...",
  "created_at": "2024-01-15T10:30:00Z",
  "output_sop_uuid": null
}`} />
            </div>

            <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
              <div className="flex items-start gap-2">
                <PenTool className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-400" />
                <div className="flex-1">
                  <p className="text-sm text-blue-200 mb-2">
                    <strong className="font-semibold">Writing effective prompts:</strong>
                  </p>
                  <ul className="text-sm text-blue-200 space-y-1 list-disc list-inside ml-2">
                    <li>Be specific about what you want to generate</li>
                    <li>Include context about the use case or domain</li>
                    <li>Mention any specific requirements or steps to include</li>
                    <li>Longer, more descriptive prompts often produce better results</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 3: Monitor Progress */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">3</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Monitor generation progress</h3>
              <p className="text-sm text-gray-400 leading-6">
                SOP generation is asynchronous. Poll the task endpoint to check status and retrieve the generated SOP UUID when complete.
              </p>
            </div>
          </div>

          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X GET ${BASE_URL}/projects/{project_uuid}/sopGenerationTasks/{task_uuid} \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (Processing)</h4>
              <CodeBlock code={`{
  "uuid": "{task_uuid}",
  "status": "PROCESSING",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "prompt": "...",
  "created_at": "2024-01-15T10:30:00Z",
  "output_sop_uuid": null
}`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (Completed)</h4>
              <CodeBlock code={`{
  "uuid": "{task_uuid}",
  "status": "COMPLETED",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "prompt": "...",
  "created_at": "2024-01-15T10:30:00Z",
  "completed_at": "2024-01-15T10:34:30Z",
  "output_sop_uuid": "f6a7b8c9-d0e1-2345-f012-456789012345"
}`} />
            </div>

            <div className="p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
              <p className="text-sm text-yellow-200 flex items-start gap-2">
                <Timer className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong className="font-semibold">Processing time:</strong> SOP generation typically takes 2-5 minutes,
                  depending on document complexity and prompt length. Poll every 10-15 seconds.
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Step 4: Retrieve SOP */}
        <div className="card">
          <div className="flex items-start gap-4 mb-6">
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-lg">4</span>
            <div className="flex-1">
              <h3 className="text-xl font-bold tracking-tight text-gray-200 mb-2">Retrieve the generated SOP</h3>
              <p className="text-sm text-gray-400 leading-6">
                Once the task status is <code className="text-green-400 bg-gray-800 px-1 py-0.5 rounded">COMPLETED</code>,
                use the <code className="text-purple-400 bg-gray-800 px-1 py-0.5 rounded">output_sop_uuid</code> to fetch the complete SOP.
              </p>
            </div>
          </div>

          <div className="ml-14 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Request</h4>
              <CodeBlock code={`curl -X GET ${BASE_URL}/sops/{sop_uuid} \\
  -H "X-API-Key: your-email@example.com:your_api_key_here"`} />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-300 mb-2">Response</h4>
              <CodeBlock code={`{
  "uuid": "{sop_uuid}",
  "title": "Standard Operating Procedure: Customer Support Escalation Handling",
  "content": "This SOP provides a comprehensive guide for handling customer support escalations...",
  "steps": [
    {
      "step_number": 1,
      "title": "Identify Escalation Criteria",
      "description": "Recognize when an inquiry requires escalation...",
      "detailed_steps": [
        "Review customer inquiry details",
        "Check issue severity level",
        "Verify customer status",
        "Determine if escalation is needed"
      ]
    },
    {
      "step_number": 2,
      "title": "Triage and Categorize",
      "description": "Classify the escalation type and assign priority...",
      "detailed_steps": [
        "Categorize escalation type (technical, billing, account)",
        "Assign priority level (low, medium, high, urgent)",
        "Route to appropriate team",
        "Set SLA expectations"
      ]
    },
    {
      "step_number": 3,
      "title": "Execute Escalation",
      "description": "Follow the escalation path and notify stakeholders...",
      "detailed_steps": [
        "Notify escalation handler",
        "Transfer ticket to appropriate team",
        "Update customer on status",
        "Document escalation reason"
      ]
    }
  ],
  "created_at": "2024-01-15T10:34:30Z",
  "project_uuid": "{project_uuid}"
}`} />
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="card mt-8 bg-gray-900 border border-gray-800">
          <h3 className="text-lg font-bold tracking-tight text-gray-200 mb-3">Best Practices</h3>
          <div className="space-y-4 text-sm text-gray-300">
            <div>
              <strong className="text-gray-200">Prompt engineering:</strong>
              <p className="text-gray-400 mt-1 leading-6">
                Write detailed prompts that clearly describe the desired SOP. Include context, specific requirements, and any
                constraints. Example: "Generate an SOP for onboarding new employees. Include pre-arrival, first day, and first week activities."
              </p>
            </div>
            <div>
              <strong className="text-gray-200">Document quality:</strong>
              <p className="text-gray-400 mt-1 leading-6">
                Ensure your namespace contains high-quality, relevant documents. More comprehensive source material produces
                more detailed and accurate SOPs.
              </p>
            </div>
            <div>
              <strong className="text-gray-200">Iterative improvement:</strong>
              <p className="text-gray-400 mt-1 leading-6">
                Generate multiple SOPs with different prompts to refine the output. Each generation learns from your namespace
                documents and prompt clarity.
              </p>
            </div>
            <div>
              <strong className="text-gray-200">Error handling:</strong>
              <p className="text-gray-400 mt-1 leading-6">
                If a task fails (status: <code className="text-red-400 bg-gray-800 px-1 py-0.5 rounded">FAILED</code>),
                check the task response for error details. Common issues include insufficient documents in the namespace or
                malformed prompts.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SOPGenerationWorkflow;
